import React, { Component } from 'react'
import { FieldArray } from 'formik'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { Modal, Container, Segment, Message, Grid, DropdownItemProps, Input as PInput, Dropdown as PDropdown, Header, Icon, MessageContent, MessageHeader } from 'semantic-ui-react'
import { AxiosInstance } from 'axios'
import { withTranslation, WithTranslation } from 'react-i18next'
import { CustomParams, Meta, Query, LocalizedLabels, RatatoskRequest, RatatoskPage, RatatoskResponse, getLocalizedLabel, getmeta, featureValueTypes, Feature, collectFeatureOptions, createDefaultFeatureLabel } from '../common'
import { Md5 } from 'ts-md5' 
import FeatureQuery, { addDefaultFeature} from './form/FeatureQuery'
import SlotQuery, { addDefaultSlot } from './form/SlotQuery'
import ResultList from './render/ResultList'
import CNCFormList from './render/CNCFormList'
import CNCLemmaList from './render/CNCLemmaList'
import CNCFormListHelp from './render/CNCFormListHelp'
import CNCLemmaListHelp from './render/CNCLemmaListHelp'
import NameQuery, { addDefaultName } from './form/NameQuery'
import Form from './form/Form'
import Button from './form/Button'
import Batch from './Batch'
import InputFeatureType from './form/InputFeatureType'
import Delete from './Delete'
import AcPhraseList from './render/AcPhraseList'
import MenuQuery from './form/MenuQuery'
import UserScript from './UserScript'
import CatchQuery, { addDefaultCatch } from './form/CatchQuery'
import { CustomComponents } from './Comp'
import LemurHelp from './render/LemurHelp'
import AdvancedQuery from './form/AdvancedQuery'

const stateRevision = "2"

interface SearchProps extends WithTranslation, RouteComponentProps {
    meta: Meta,
    http: AxiosInstance,
    checkUpdates: (data: any) => void
}

type ResultListParams = {
    columns: [
        {
            label: {},
            target?: string,
            path: string,
            link?: string
        }
    ],
    view?: boolean,
    edit?: boolean
}

export interface ResultListProps extends WithTranslation, RouteComponentProps {
    response: RatatoskResponse,
    pageInfo: PageInfo,
    meta: Meta,
    params: ResultListParams,
    directRequest?: boolean,
    loading: boolean,
    selectId: (id: string, select: boolean) => void | null,
    selectedIds: string[],
    viewOptionsOpen: () => void
}

interface SearchState {
    type: string,
    query_comps: Query[],
    query_sum: string,
    lastQuery: Query|null,
    batchQuery: Query|null,
    page: RatatoskPage,
    response: RatatoskResponse|null,
    authorized: boolean,
    errorMsg: string,
    sessionExpired: boolean,
    searchWarning: string,
    submitting: boolean,
    directRequest: boolean,
    helpIsOpen: boolean,
    newDialogOpen: boolean,
    batchDialogOpen: string|null,
    deleteDialogOpen: string|null,
    batchMessage: string,
    userScriptDialog: string,
    customCompDialog: any,
    customCompParams: string[],
    newName: string,
    newType: string,
    selectedIds: string[],
    viewOptionsDialogOpen: boolean,
    customColumns: { path: string }[]
}

interface FormComponent {
    label: LocalizedLabels,
    comp: string,
    params: CustomParams,
    values: {}
}

interface SearchParams {
    search: string,
    request_url: string,
    request_defaults: {}
    form: FormComponent[],
    urlparams: string[],
    render: {},
    help: {}
}

export interface PageInfo {
    pageStart: number,
    pageEnd: number,
    total: number,
    curPage: number,
    totalPages: number,
    pagination: boolean,
    pageChange: (e,d) => void
}


class Search extends Component<SearchProps, SearchState> {

    default_state : SearchState = {
        type: '',
        query_comps: [],
        query_sum: '',
        lastQuery: null,
        batchQuery: {},
        page: { from: 0, size: 100 },
        response: null,
        authorized: true,
        errorMsg: '',
        sessionExpired: false,
        searchWarning: '',
        submitting: false,
        directRequest: false,
        helpIsOpen: false,
        newDialogOpen: false,
        batchDialogOpen: null,
        deleteDialogOpen: null,
        userScriptDialog: '',
        customCompDialog: null,
        customCompParams: [],
        batchMessage: '',
        newName: '',
        newType: '',
        selectedIds: [],
        viewOptionsDialogOpen: false,
        customColumns: []
    }

    state = this.default_state

    formComponents = {
        NameQuery: NameQuery,
        CatchQuery: CatchQuery,
        FeatureQuery: FeatureQuery,
        SlotQuery: SlotQuery,
        MenuQuery: MenuQuery,
        AdvancedQuery: AdvancedQuery
    }

    queryComposers = {
        NameQuery: addDefaultName,
        CatchQuery: addDefaultCatch,
        FeatureQuery: addDefaultFeature,
        SlotQuery: addDefaultSlot,
        MenuQuery: addDefaultFeature,
        AdvancedQuery: (query, params) => {}
    }

    renderComponents = {
        ResultList: ResultList,
        CNCFormList: CNCFormList,
        CNCLemmaList: CNCLemmaList,
        AcPhraseList: AcPhraseList
    }

    helpComponents = {
        CNCFormListHelp: CNCFormListHelp,
        CNCLemmaListHelp: CNCLemmaListHelp,
        LemurHelp: LemurHelp,
    }

    initialized = false

    defaultState(type: string, params: SearchParams & {request_defaults: {[key: string]: any}}) {
        let comps = params.form
        let query_comps : Query[] = []
        for (let comp of comps) {
            const default_query: any = {}
            const composer = this.queryComposers[comp.comp]
            composer(default_query, comp.params)
            query_comps.push(default_query)
        }
        // maybe find a better solution for the "type" constraint - shall we expect a multitype search with type query/selection, too??
        if (params.search) query_comps.push({type: params.search})
        let state = this.default_state
        state.type = type
        state.query_comps = query_comps
        state.page = params.request_defaults.page_size ? {from: 0, size: params.request_defaults.page_size} : state.page
        return state
    }

    saveState() {
        sessionStorage.setItem('state_search_'+this.state.type, JSON.stringify(this.state))
        localStorage.setItem('state_search_'+this.state.type, JSON.stringify(this.state))
    }

    loadState(type: string): boolean {
        let storedState : string|null = sessionStorage.getItem('state_search_'+type) || localStorage.getItem('state_search_'+type)
        if (storedState) {
            this.setState(JSON.parse(storedState))
            return true
        }
        return false
    }

    componentDidMount() {
        this.componentDidUpdate(this.props, this.state)
    }

    componentDidUpdate(prevProps: SearchProps, prevState: SearchState) {
        const { location, meta, history } = this.props
        
        // not authorized => login
        //if (!this.state.authorized) {return(<Redirect to={{ pathname: "/login", state: { from: location } }} />)}

        let urlparams = location.pathname.replace(/^\/search\//,"").split('/')
        let curtype = urlparams.shift() as string
        curtype = curtype.replaceAll('_',':')
        // global (empty) prefix is avoided in URL for simple types
        if (!curtype.includes(':')) { curtype = ':' + curtype }
        if (!this.initialized || location.pathname !== prevProps.location.pathname) {
            
            // get metadata configuration of current form type and reset its state
            urlparams = urlparams.filter((x) => x !== '')
            if (!(meta.uis['search'] && curtype)) return
            if (!(curtype in meta.uis['search'])) curtype = ':' + curtype
            if (!(curtype in meta.uis['search'])) return
            this.initialized = true
            let params = meta.uis['search'][curtype]['params']

            // if current form definition is empty, wait for TopMenu to redirect us to a non-empty type
            // (previously: fall back to the first subtype in order)
            if (!params?.search) {
                /*
                const subitems: any[] = Object.values(meta.uis['search']).filter((i: any) => i.type.startsWith(curtype+':')).sort(searchTypeOrder)
                if (subitems.length) {
                    curtype = subitems[0].type
                    const parts = curtype.split(':')
                    // for global single level types just use the second level ID; otherwise concatanate all parts with underscores
                    const key = parts.length === 2 && !parts[0] ? parts[1] : parts.join('_')
                    const path = "/search/" + key
                    history.push(path)
                    return
                } else {*/
                    // missing search form definition
                    return
                //}
            }
            
            // direct request - process independently
            if (urlparams.length && 'urlparams' in params) {
                let state = this.defaultState(curtype, params)
                state.directRequest = true
                for (let i in params['urlparams']) {
                    let value = urlparams[i]
                    if (value && value!='_') {
                        state.query_comps = this.updateQuery(state.query_comps, params['urlparams'][i], value)
                    }
                }
                this.runQuery(state, null)
                return
            }

            let sum = Md5.hashStr(JSON.stringify(params)+stateRevision)
            let storedStateSum : string|null = sessionStorage.getItem('sum_state_search_'+curtype) || localStorage.getItem('sum_state_search_'+curtype)
            let loaded = false
            if (storedStateSum === sum) {
                loaded = this.loadState(curtype)
            }
            if (!loaded) {
                sessionStorage.setItem('sum_state_search_'+curtype, sum)
                sessionStorage.removeItem('state_search_'+curtype)
                localStorage.setItem('sum_state_search_'+curtype, sum)
                localStorage.removeItem('state_search_'+curtype)
                this.setState(this.defaultState(curtype, params))
            }
        }
        if (curtype === prevState.type && 
            (
                this.state.page.from !== prevState.page.from || 
                this.state.page.size !== prevState.page.size || 
                (this.state.customColumns.filter(x => !prevState.customColumns.includes(x)).length)
            )) {
            this.sendQuery(this.state, false, false)
        }
    }

    componentWillUnmount() {
        this.initialized = false
    }


    validFeatureQuery = (f: any) => {
        // this should not really happen anymore: f.method === undefined
        if (f.method === 'defined' /*|| f.method === undefined*/ || (f.value !== '' && f.value !== null && f.value !== undefined && !(Array.isArray(f.value) && f.value.length===0)))
            return true
        return false
    }

    consolidateFeatureQuery(f: any) {
        // treat different search methods,
        // (string case-insensitivity (f.ci) is treated by the backend)
        if (f.method) {
            if (f.method === 'defined') {
                delete f.value
            } else if (f.method === 'prefix') {
                f.value += '*'
            } else if (f.method === 'regexp') {
                f.value = {regexp: f.value}
            } else if (f.method === 'allflags') {
                f.value = {fulltext: Array.isArray(f.value) ? f.value.join(" ") : f.value}
                delete f.ci
            } else if (f.method === 'anyflag') {
                f.value = {anyflag: Array.isArray(f.value) ? f.value.join(" ") : f.value}
                delete f.ci
            } else if (f.method === 'fulltext') {
                f.value = {fulltext: f.value}
                delete f.ci
            } else if (['lt', 'lte', 'gt', 'gte'].includes(f.method)) {
                const value = f.value
                f.value = {range: {}}
                f.value.range[f.method] = value
            } else {
                // exact match
                // do not allow the user enforcing a prefix search manually
                if (typeof f.value === 'string' || f.value instanceof String)
                    f.value = f.value.replace(/([^\\])\*$/,"$1\\*")
            }
        }
        f.type = f.type.replace(/::[0-9]+$/, '')
        if (f.subspec) {
            f.type = [f.type, ...f.subspec].join(':')
        }
        if (f.aggrspec) {
            f.type = [f.type, f.aggrspec].join(':')
        }
        if (f.within) {
            f.__within = f.within.join(' ')
        }
        if (f.updated_on_from) {
            f.updated_on = {range: {}}
            f.updated_on.range['gte'] = f.updated_on_from
        }
        if (f.updated_on_to) {
            if (!f.updated_on)
                f.updated_on = {range: {}}
            f.updated_on.range['lte'] = f.updated_on_to
        }
        f.type = f.type.replace(/:$/, '')
        delete f.subspec
        delete f.aggrspec
        delete f.method
        delete f.within
        delete f.updated_on_from
        delete f.updated_on_to
        if (!f.updated_by)
            delete f.updated_by
        const neg = f.neg
        delete f.neg
        if (neg) {
            f = {__not: f}
        }
        return f
    }

    consolidateNameQuery(nameReq: any) {
        let value = nameReq.value
        if (nameReq.method === 'regexp') 
            value = {regexp: value}
        else if (nameReq.method === 'fulltext')
            value = {fulltext: value}
        else if (nameReq.method === 'prefix')
            value = value+'*'
        else value = value.replace(/([^\\])\*$/,"$1\\*")
        if (nameReq.ci && nameReq.method !== 'fulltext') 
            value = {ci: value}
        return value
    }

    consolidateQuery(qComps: Query[]) : Query|null {
        let query: Query = {}
        let validq = false
        query.feats = []
        for (let subQuery of qComps) {
            if (subQuery['name'] && subQuery['name'].value) {
                const f = JSON.parse(JSON.stringify(subQuery['name']))
                query['name'] = this.consolidateNameQuery(f)
                validq = true
            }
            if (subQuery['type']) {
                query['type'] = subQuery['type']
            }
            if (subQuery['_custom']) {
                for (let field in subQuery['_custom']) {
                    if (subQuery['_custom'][field]) {
                        if (!query._custom) query._custom = {}
                        query._custom[field] = subQuery['_custom'][field]
                        validq = true
                    }
                }
            }
            if (subQuery['updated_by']) {
                query.updated_by = subQuery['updated_by']
            }
            if (subQuery.updated_on_from) {
                query.updated_on = {range: {}}
                query.updated_on.range['gte'] = subQuery.updated_on_from
            }
            if (subQuery.updated_on_to) {
                if (!query.updated_on)
                    query.updated_on = {range: {}}
                query.updated_on.range['lte'] = subQuery.updated_on_to
            }
            if (subQuery.feats) {
                for (let f of subQuery.feats) {
                    f = JSON.parse(JSON.stringify(f))
                    if (this.validFeatureQuery(f)) {
                        query.feats.push(this.consolidateFeatureQuery(f))
                        validq = true
                    }
                }
            }
            // consolidate slots
            if (subQuery.slots) {
                for (let s of subQuery.slots) {
                    s = JSON.parse(JSON.stringify(s))
                    if (!query.slots) {
                        query.slots = []
                    }
                    if (!s.pos) delete s['pos']
                    let sfeats: Feature[] = []
                    for (let fi in s.feats) {
                        const f = s.feats[fi]
                        if (this.validFeatureQuery(f)) {
                            sfeats.push(this.consolidateFeatureQuery(f))
                            validq = true
                        }
                    }
                    // consolidate fillers
                    for (let fli in s.fillers) {
                        let ffeats: Feature[] = []
                        for (let fi in s.fillers[fli].feats) {
                            const f = s.fillers[fli].feats[fi]
                            if (this.validFeatureQuery(f)) {
                                ffeats.push(this.consolidateFeatureQuery(f))
                                validq = true
                            }
                        }
                        if (ffeats.length) s.fillers[fli].feats = ffeats
                    }
                    if (sfeats.length) s.feats = sfeats
                    query.slots.push(s)
                }
            }
        }
        if (validq)
            return query
        else
            return null
    }

    updateQuery (comps: Query[], path: string, value: string|number) : Query[] {
        let pathl = path.split('.')
        const last = pathl.pop()
        let target : any = comps
        for (let step of pathl) {
            if (target && step in target) {
                target = target[step]
            } else {
                target = null
            }
        }
        if (last && target !== null) {
            target[last as string] = value
        }
        return comps
    }

    runQuery(state: any, formik: any, values: Query[]|undefined = undefined) {
        if (formik) {
            formik.setSubmitting(true)
        }
        if (values) {
            this.setState({query_comps: values, page: { from: 0, size: state.page.size }, errorMsg: '', searchWarning: '', directRequest: false}, () => {
                this.saveState()
                this.sendQuery(this.state, false)
            })
        } else {
            this.setState(state, () => this.sendQuery(this.state, true))
        }
        if (formik) {
            formik.setSubmitting(false)
        }
    }

    sendQuery(state: any, directRequest = false, newQuery = true) {
        const { meta, checkUpdates, t } = this.props
        const { customColumns } = this.state
        const params = meta.uis['search'][state.type]['params']
        let query = this.consolidateQuery(state.query_comps)
        if (!query) {
            this.setState({searchWarning: t('No search criteria entered.')})
            return
        }
        let request: RatatoskRequest = {query: {}, page: state.page}
        request.sort = []
        if (params.request_defaults) {
            if (params.request_defaults.feats)
                request.feats = params.request_defaults.feats.concat(customColumns.map(f => f.path))
            if (params.request_defaults.page_size)
                request.page.size = params.request_defaults.page_size
            if (params.request_defaults.sort) {
                for (let sort of params.request_defaults.sort) {
                    // TODO: needs testing! (or rather transparent reimplementation in the backend)
                    let feature = sort.feature + (sort.subspec ? ':'+sort.subspec : '')
                    const fmeta = getmeta(meta, 'feature', feature)
                    // common feature value
                    const prefix = featureValueTypes[fmeta.params.value_type].prefix
                    let fvalue = prefix+'value'
                    // agregated feature value
                    if (fmeta.params.aggregate) {
                        let subparts = sort.subspec.split(':')
                        const lastpart = subparts.pop()
                        if (subparts.length && (!fmeta.subspec_required || subparts.length>fmeta.params.subspecs.length) && lastpart!=fmeta.params.aggregate.default_spec) {
                            feature = [sort.feature, ...subparts].join(':')
                            if (fmeta.params.aggregate.indexed) {
                                fvalue = prefix+'sub:'+lastpart
                            } else {
                                fvalue = '_fl_values.'+lastpart
                            }
                        }
                    }
                    let sortitem = {}
                    sortitem['feats.'+fvalue] = {
                        order: sort.order,
                        nested: {
                            path: 'feats',
                            filter: {term: {'feats.type': feature}}
                        }
                    }
                    request.sort.push(sortitem)
                }
            }
        }
        request.sort.push({'name.cs_sort':{'order':'asc'}})
        request.query = query
        request.page = state.page
        const query_sum = Md5.hashStr(JSON.stringify(request))
        let newState = {submitting: true, errorMsg: '', directRequest: directRequest, lastQuery: null}
        if (newQuery) newState['selectedIds'] = []
        this.setState(newState)
        const url = params.request_url || 'cunits/_view'
        this.props.http.post(url, request)
            .then(response => {
                this.setState({response: response.data, errorMsg: '', submitting: false, query_sum: query_sum, lastQuery: query}, () => {
                    checkUpdates(response.data)
                    if (!directRequest) {
                        this.saveState()
                    }
                })
            }).catch(err => {
                if (err.response) {
                    // UNAUTHORIZED
                    if (err.response.status===401) {
                        let newState = {errorMsg: err.response.data.message, submitting: false, authorized: false}
                        if (err.response._user_id!==meta.user['id']) newState['sessionExpired'] = true
                        this.setState(newState)
                    } else {
                        this.setState({submitting: false, errorMsg: err.response.data.message, response: null}, () => this.saveState())
                    }
                }
            })
    }


    createNew() {
        const { history, meta, t } = this.props
        const { newName, newType } = this.state
        const url = 'cunits/_new'
        let newCUnit = {
            type: newType,
            name: newName,
            slots: [],
            feats: []
        }
        this.setState({submitting: true})
        this.props.http.put(url, newCUnit)
            .then(response => {
                const newPath = response.data.data[0]['_path']
                history.push('/edit/'+newPath)
                this.setState({response: response.data, newDialogOpen: false, errorMsg: '', submitting: false})
            }).catch(err => {
                if (err.response) {
                    // UNAUTHORIZED
                    if (err.response.status===401) {
                        let newState = {newDialogOpen: false, errorMsg: err.response.data.message, submitting: false}
                        if (err.response._user_id!==meta.user['id']) newState['sessionExpired'] = true
                        this.setState(newState)
                    } else {
                        console.log(err.response.data.message)
                        this.setState({newDialogOpen: false, errorMsg: err.response.data.message, submitting: false})
                    }
                }
                window.scrollTo(0, 0)
            })
    }

    clearResponse() {
        this.setState({response: null})
    }

    render () {
        const { location, meta, http, i18n, t, history } = this.props
        const { response, page, query_sum, submitting, helpIsOpen, directRequest, newDialogOpen, batchDialogOpen, deleteDialogOpen, lastQuery,
            batchQuery, batchMessage, selectedIds, customColumns, viewOptionsDialogOpen, userScriptDialog, customCompDialog, customCompParams } = this.state

        if (location.pathname.split('/').filter(x => x!=='').length === 1) {
            const last = sessionStorage.getItem('last_search') || localStorage.getItem('last_search')
            if (last) {
                return (<Redirect to={last}/>)
            }
            else {
                const first = Object.keys(meta.uis['search'])[0]
                return (<Redirect to={"/search/"+first.replace(/^:/,'').replace(':','_')}/>)
            }
        } else {
            sessionStorage.setItem('last_search', location.pathname)
            localStorage.setItem('last_search', location.pathname)
        }

        //if (!this.state.authorized) {return(<Redirect to={{ pathname: "/login", state: { from: location } }} />)}    

        const curtype = this.state.type
        if (!(meta.uis['search'] && curtype in meta.uis['search'] && meta.uis['search'][curtype]['_can_read'] && meta.cats['cunit'])) {
            return(null)
        }
        const params = meta.uis['search'][curtype]['params']
        if (!this.state.query_comps.length) {
            return(null)
        }

        const hiddenForm = params.hidden
        
        const ResultRenderer = this.renderComponents[params['render']['comp']]
        let resultRendererParams = {}
        if ('params' in params['render']) {
            resultRendererParams = JSON.parse(JSON.stringify(params['render']['params']))
            resultRendererParams['columns'] = resultRendererParams['columns'] ? resultRendererParams['columns'].concat(customColumns) : customColumns
        }

        const HelpRenderer = params['help'] && params['help']['comp'] ? this.helpComponents[params['help']['comp']] : null

        const pageChange = (e, d) => {
            const { size } = this.state.page
            const pgnum = d.activePage
            const newStart = (pgnum-1) * size
            this.setState({page:{from: newStart, size: size}}, () => this.saveState())
        }

        let pageInfo: PageInfo = {pageStart: page.from + 1, pageEnd: page.from+page.size,  curPage: 0, totalPages: 0, pagination: false, total: -1, pageChange: pageChange}
        if (response && response.total) {
            if (response.total.value > page.size) {
                pageInfo.pagination = true
                // ElasticSearch search API only allows access to the first 10000 results (by standard means)
                pageInfo.totalPages = response.total.value > 10000 ? Math.ceil(10000 / page.size) : Math.ceil(response.total.value / page.size)
                pageInfo.curPage = (page.from / page.size) + 1
            }
            if (page.from+page.size > response.total.value) pageInfo.pageEnd = response.total.value
            if (response.total.relation !== 'gte') pageInfo.total = response.total.value
        }

        const helpClose = () => { this.setState({ helpIsOpen: false }) }
        const helpOpen = () => {
            this.setState({ helpIsOpen: true })
        }
        const helpContent = (<HelpRenderer/>)


        let newUnitTypeOptions: DropdownItemProps[] = []
        let cunitTypes : string[] = []
        /*if (!params.search.endsWith('*')) {
            const i = getmeta(meta, 'cunit', params.search)
            cunitTypes.push(params.search)
            if (i['_can_write'])
                newUnitTypeOptions = [{key: params.search, value: params.search, text: getLocalizedLabel(i.label, i18n.language)}]
        } else {*/
        newUnitTypeOptions = Object.keys(meta.cats['cunit']).reduce((sel, cutype) => {
            const m = meta.cats['cunit'][cutype]
            if (
                (!m.subdef_required) && (
                    (params.search.endsWith('*') && cutype.startsWith(params.search.substr(0,params.search.length-1))) ||
                    (cutype === params.search || cutype.startsWith(params.search+':'))
                )
            ) {
                cunitTypes.push(cutype)
                if (m['_can_write'])
                    sel.push({key: cutype, value: cutype, text: getLocalizedLabel(m.label, i18n.language)})
            }
            return sel
        }, [] as DropdownItemProps[])
        //}

        const batchOpen = (data) => {
            const action = data.value
            const message = data.message
            if (action) {
                if (action === 'batchAll' && lastQuery) {
                    this.setState({ batchDialogOpen: action, batchMessage: message, batchQuery: lastQuery })
                } else if (action === 'batchSel') {
                    let selQuery = {'_id': selectedIds}
                    if (lastQuery && lastQuery['type']) selQuery['type'] = lastQuery['type']
                    this.setState({ batchDialogOpen: action, batchMessage: message, batchQuery: selQuery })
                } else if (action === 'deleteAll' && lastQuery) {
                    this.setState({ deleteDialogOpen: action, batchMessage: message, batchQuery: lastQuery })
                } else if (action === 'deleteSel') {
                    let selQuery = {'_id': selectedIds}
                    if (lastQuery && lastQuery['type']) selQuery['type'] = lastQuery['type']
                    this.setState({ deleteDialogOpen: action, batchMessage: message, batchQuery: selQuery })
                } else {
                    const stype = action.replace(/:__(sel|all)__$/, '')
                    this.setState({userScriptDialog: stype, batchQuery: action.endsWith(':__sel__') ? {'_id': selectedIds} : lastQuery})
                }
            }
        }
        const batchClose = (reset: boolean = false) => {
            let state = { batchDialogOpen: null, batchMessage: ''}
            if (reset===true) state['response'] = null
            this.setState(state, () => this.saveState())
        }
        const deleteClose = (reset: boolean = false) => {
            let state = { deleteDialogOpen: null, batchMessage: ''}
            if (reset===true) state['response'] = null
            this.setState(state) 
        }
        const selectId = newUnitTypeOptions.length && response && response.total?.value ? // newUnitTypeOptions => user has write permissions
        (id: string, select: boolean) => {
            const { selectedIds } = this.state
            if (select) this.setState({selectedIds: [...selectedIds, id]}, () => this.saveState())
            else this.setState({selectedIds: selectedIds.filter(i => i !== id)}, () => this.saveState())
        } : null
        
        const newClose = () => { this.setState({ newDialogOpen: false }) }
        const newOpen = () => {
            const newType = this.state.newType && newUnitTypeOptions.map(x => x.value).includes(this.state.newType) ?
                this.state.newType : String(newUnitTypeOptions[0].value)
            this.setState({ newDialogOpen: true, newType: newType })
        }

        const viewOptionsClose = () => { this.setState({ viewOptionsDialogOpen: false }) }
        const viewOptionsOpen = () => { this.setState({ viewOptionsDialogOpen: true }) }

        const openCustomComponent = (comp: any, params: string[]=[]) => {
            if (!comp.params || comp.params.length===params.length) {
                const url = comp.params ? comp.params.reduce((res, p, i) => res.replace(RegExp(`__%${i+1}__`), params[i]), comp.path) : comp.path
                history.push(url)
            } else {
                const emptyParams = comp.params.map(p => '')
                this.setState({customCompDialog: comp, customCompParams: emptyParams})
            }
        }

        const showSubmit = params['form'].some((item: FormComponent) => !this.formComponents[item.comp]['HIDE_SUBMIT'])

        const rescountstr = " ("+response?.total?.value+")"
        const selcountstr = " ("+selectedIds.length+")"

        let batchOptions: any[] = []
        if (response?.total?.value) {
            batchOptions.push(<PDropdown.Item value='batchAll' text={t('Batch update all')+rescountstr} message={t('Batch update all units found by the query')+rescountstr} onClick={(e,d)=>batchOpen(d)}/>)
                //{key: 'batchAll', value: 'batchAll', text: t('Batch update all')+rescountstr, message: t('Batch update all units found by the query')+rescountstr})
            if (selectedIds.length) { 
                batchOptions.push(<PDropdown.Item value='batchSel' text={t('Batch update selected')+selcountstr} message={t('Batch update selected units')+selcountstr} onClick={(e,d)=>batchOpen(d)}/>)
            }
            batchOptions.push(<PDropdown.Item value='deleteAll' text={t('Delete all units')+rescountstr} message={t('Delete all units found by the query')+rescountstr} onClick={(e,d)=>batchOpen(d)}/>)
            if (selectedIds.length) { 
                batchOptions.push(<PDropdown.Item value='deleteSel' text={t('Delete selected units')+selcountstr} message={t('Delete selected units')+selcountstr} onClick={(e,d)=>batchOpen(d)}/>)
            }
        }

        // find user accessible scripts for import, update and export of entries
        const mycutype = params.search.replace(/\*$/,'')
        if (meta.resources && meta.resources['user_bin']) {
            const availableScripts = Object.values(meta.resources['user_bin']).reduce((acc: any[], s:any) => {
                const m = meta.resources['user_bin'][s.type]
                if (m._can_read && m.params?.cunit_type?.filter(x => mycutype.startsWith(x)).length)
                    acc.push(s)
                return acc
            }, []).sort((a: any, b: any) => {
                const alabel = getLocalizedLabel(a.label, i18n.language)
                const blabel = getLocalizedLabel(b.label, i18n.language)
                return alabel.localeCompare(blabel)
            })

            const importScripts = availableScripts.filter((x: any) => x.params?.type === 'import')
            const updateScripts = availableScripts.filter((x: any) => x.params?.type === 'update')
            const exportScripts = availableScripts.filter((x: any) => x.params?.type === 'export')

            if (importScripts.length) {
                batchOptions.push(<PDropdown.Divider/>)
                for (let s of importScripts) {
                    batchOptions.push(<PDropdown.Item value={s.type} text={getLocalizedLabel(s.label, i18n.language)} message='' onClick={(e,d)=>batchOpen(d)}/>)
                }
            }
            if (updateScripts.length) {
                batchOptions.push(<PDropdown.Divider/>)
                for (let s of updateScripts) {
                    batchOptions.push(<PDropdown.Item value={s.type} text={getLocalizedLabel(s.label, i18n.language)} message='' onClick={(e,d)=>batchOpen(d)}/>)
                }
            }
            if (exportScripts.length && response?.total?.value) {
                batchOptions.push(<PDropdown.Divider/>)
                for (let s of exportScripts) {
                    if (selectedIds.length) {
                        batchOptions.push(<PDropdown.Item value={s.type+":__sel__"} text={getLocalizedLabel(s.label, i18n.language)+t(' - selected')+selcountstr} message='' onClick={(e,d)=>batchOpen(d)}/>)
                    }
                    batchOptions.push(<PDropdown.Item value={s.type+":__all__"} text={getLocalizedLabel(s.label, i18n.language)+t(' - all')+rescountstr} message='' onClick={(e,d)=>batchOpen(d)}/>)
                }
            }
        }

        // custom components for unit types (only) that the current user is allowed to write
        const customComponents = CustomComponents.filter((c) => newUnitTypeOptions.map(o => o.value).includes(c.cunit_type))
            .map(c => <PDropdown.Item text={t(c.label)} onClick={(e,d)=>openCustomComponent(c)}/>)
        
        return (
            <Container>
                <Modal content={helpContent} open={helpIsOpen} onClose={helpClose} centered={false} closeIcon/>

                <Modal open={newDialogOpen}>
                    <Modal.Content>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                <PInput label={t('Name')} defaultValue={this.state.newName} fluid={true}
                                    error={!this.state.newName.trim()}
                                    onChange={(e,d)=>this.setState({newName: d.value})}/>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                <PDropdown label={t('Type')} defaultValue={this.state.newType} fluid={true}
                                    options={newUnitTypeOptions} selection
                                    onChange={(e,d)=>this.setState({newType: String(d.value)})}/>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button color="red" onClick={newClose}>{t('Cancel')}</Button>
                        <Button color="blue" disabled={!this.state.newName.trim()}
                            onClick={()=>this.createNew()}>{t('Create')}</Button>
                    </Modal.Actions>
                </Modal>
                
                <Modal open={customCompDialog}>
                    <Modal.Content>
                        <Grid>
                        { customCompDialog && customCompDialog.params.map((p, i) =>
                            <Grid.Row>
                                <Grid.Column width={16}>
                                <PInput label={t(p.label)} value={customCompParams[i]} fluid={true}
                                    error={!customCompParams[i].trim()}
                                    onChange={(e,{value})=>{
                                        customCompParams.splice(i,1,value)
                                        this.setState({customCompParams: customCompParams})
                                    }}/>
                                </Grid.Column>
                            </Grid.Row>
                        )}
                        </Grid>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button color="red" onClick={()=>this.setState({customCompDialog: null})}>{t('Cancel')}</Button>
                        <Button color="blue" disabled={!customCompDialog || customCompParams.filter(x=>x).length!==customCompDialog.params.length} 
                            icon="play" onClick={()=>openCustomComponent(customCompDialog, customCompParams)}/>
                    </Modal.Actions>
                </Modal>

                <Modal open={!!batchDialogOpen} centered={false} onClose={() => batchClose(false)} closeIcon>
                    <Modal.Content>
                        <Batch {...this.props} meta={meta} http={http} message={batchMessage} batchId={this.state.type} 
                            query={batchQuery||{}} close={batchClose}/>
                    </Modal.Content>
                </Modal>
                <Modal open={!!deleteDialogOpen} centered={false} onClose={() => deleteClose(false)} closeIcon>
                    <Modal.Content>
                        <Delete {...this.props} meta={meta} http={http} message={batchMessage} batchId={this.state.type} 
                            query={batchQuery||{}} close={deleteClose}/>
                    </Modal.Content>
                </Modal>
                <Modal open={!!userScriptDialog} centered={false} size="large" closeIcon={true} onClose={() => this.setState({userScriptDialog: ''})}>
                    <Modal.Content>
                        <UserScript {...this.props} meta={meta} http={http} type={userScriptDialog} query={batchQuery}
                            params={resultRendererParams} close={()=>this.setState({userScriptDialog: ''})}/>
                    </Modal.Content>
                </Modal>
                <Modal size="large" open={!!viewOptionsDialogOpen}>
                    <Modal.Header>{t('View options')}</Modal.Header>
                    <Modal.Content>
                        <Form key={this.state.type+'_view_options'} initialValues={{features: customColumns}} className="searchform"
                            onSubmit={(values, formikApi) => {
                                formikApi.setSubmitting(false)
                                this.setState({customColumns: values.features, viewOptionsDialogOpen: false}, () => this.saveState())
                            }}>
                                {(formik) => {
                            return(
                            <>
                            <Segment color="grey">
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width={14}>
                                        <Header as='h3'>{t('Additional columns')}</Header>
                                    </Grid.Column>
                                </Grid.Row>
                                <FieldArray
                                    name="features"
                                    render={(subformik) => {
                                    const features = collectFeatureOptions(meta, cunitTypes, null, null, true, true, true)
                                    const typeOptions = features.map(f => ({key: f.type, text: createDefaultFeatureLabel(meta, f.type, true), value: f.type}))
                                    return (
                                        <>
                                        { formik.values.features.map((f, i) => {
                                            const fmeta = getmeta(meta, 'feature', f.path)
                                            return(
                                            <Grid.Row className='condensed'>
                                                <InputFeatureType name={'features.'+i+'.path'} fmeta={fmeta} options={typeOptions} widthCols={10}/>
                                                <Grid.Column width={5}></Grid.Column>
                                                <Grid.Column width={1} textAlign="right">
                                                    <Button type="button" color="red" icon="delete" size='mini' onClick={(e, d) => subformik.remove(i)}/>
                                                </Grid.Column>
                                            </Grid.Row>
                                            )
                                        })
                                        }
                                        <Grid.Row>
                                        <Grid.Column>
                                            <Button icon="plus" type="button" size='mini' color='blue' 
                                                onClick={(e,d) => subformik.push({path: ''})}/>
                                        </Grid.Column>
                                        </Grid.Row>
                                        </>
                                    )
                                    }}/>
                            </Grid>
                            </Segment>
                            <Segment>
                                <Grid>
                                    <Grid.Row>
                                    <Grid.Column width={16} textAlign="left">
                                        <Button.Submit>{t('Apply')}</Button.Submit>
                                        <Button type="button" color="grey" onClick={viewOptionsClose}>{t('Cancel')}</Button>
                                    </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            </Segment>
                            </>)
                            }}
                        </Form>
                    </Modal.Content>
                </Modal>
                
                <Grid>
                { this.state.errorMsg ?
                    <Grid.Row>
                        <Grid.Column>
                        <Message error icon='exclamation triangle' header={t('Error')} content={this.state.errorMsg}/>
                        {this.state.sessionExpired ?
                            <Message negative icon>
                                <Icon name="info circle"/>
                                <MessageContent>
                                    <MessageHeader>{t('Session expired')}</MessageHeader>
                                    <p>{t('Your user session has expired.')}</p>
                                    <Button type="button" color="teal" onClick={()=>window.location.reload()}><Icon name="refresh"/>{t('Reload')}</Button>
                                </MessageContent>
                            </Message>
                        : null }
                        </Grid.Column>
                    </Grid.Row>
                : null}
                { directRequest || hiddenForm ?
                <Grid.Row>
                    <Grid.Column width={16}></Grid.Column>
                </Grid.Row>
                :
                <>
                <Grid.Row>
                    <Grid.Column>
                <Form key={this.state.type} initialValues={this.state.query_comps} className="searchform"
                    onSubmit={(values: Query[], formikApi) => this.runQuery(this.state, formikApi, values)}>
                    {(formik) => (
                    <Segment color="grey">
                        <Grid>
                        <FieldArray
                        name="query_comps"
                        render={(subformik) => (
                            params['form'].map((item: FormComponent, index: number) => {
                                const Comp = this.formComponents[item.comp]
                                let label = null
                                if (item.label) {
                                    label = getLocalizedLabel(item.label, i18n.language)
                                }
                                let params = {}
                                if (item.params) { params = item.params }
                                return (
                                    <>
                                    { label ?
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <label className="label">{label}</label>
                                        </Grid.Column>
                                    </Grid.Row>
                                    : null }
                                    <Comp key={index} label={label} params={params} meta={meta} 
                                        prefix={index.toString()} formik={formik}
                                        cunitTypes={cunitTypes} clear={() => this.clearResponse()}/>
                                    </>
                                )
                            })
                        )}/>
                        { showSubmit || (params['create'] && newUnitTypeOptions.length) || HelpRenderer || customComponents.length ?
                        <Grid.Row>
                            <Grid.Column width="2">
                            { showSubmit ? <Button.Submit className={this.state.submitting ? 'loading': null}>{t('Search')}</Button.Submit> : null }
                            </Grid.Column>
                            <Grid.Column width="10">
                            </Grid.Column>
                            <Grid.Column width="4" textAlign="right">
                                { params['create'] && newUnitTypeOptions.length ? <Button type="button" color="orange" onClick={newOpen}>{t("New unit")}</Button> : null }
                                { customComponents.length ? 
                                        <PDropdown className="no-pointer" direction="left"
                                            trigger={(<Button color="blue" icon="bars"/>)}
                                            selectOnBlur={false} selectOnNavigation={false}>
                                                <PDropdown.Menu>{customComponents}</PDropdown.Menu>
                                        </PDropdown>
                                : null}
                                { HelpRenderer ? <Button type="button" color="blue" icon="help" onClick={(e,d) => helpOpen()} title={t('Help')}/> : null }
                            </Grid.Column>
                        </Grid.Row>
                        : null}
                        </Grid>
                    </Segment>
                    )}
                </Form>
                </Grid.Column>
                </Grid.Row>
                </>
                }
                { !directRequest && !hiddenForm && ResultRenderer['ALLOW_BATCH'] && newUnitTypeOptions.length && batchOptions ? // newUnitTypeOptions => user has write permissions
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Segment color="grey">
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <PDropdown text={t('Batch operations')} button color="orange" upward={false}
                                            selectOnBlur={false} selectOnNavigation={false}>
                                                <PDropdown.Menu>{batchOptions}</PDropdown.Menu>
                                        </PDropdown>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
                : null}
                { this.state.searchWarning &&
                <Grid.Row>
                    <Grid.Column>
                    <Message error icon='exclamation triangle' content={this.state.searchWarning} hidden={false}/>
                    </Grid.Column>
                </Grid.Row>
                }
                <Grid.Row>
                    <Grid.Column width={16}>
                        { ResultRenderer ? (
                            <ResultRenderer {...this.props} key={query_sum} response={response} meta={meta} pageInfo={pageInfo} viewOptionsOpen={viewOptionsOpen}
                                directRequest={directRequest} params={resultRendererParams} loading={submitting} selectedIds={selectedIds} selectId={selectId}/>
                        ) : null }
                    </Grid.Column>
                </Grid.Row>
                </Grid>
            </Container>
        )
    }
}

export default withTranslation()(Search)
